import React, { CSSProperties, ReactNode, useRef, useState } from 'react';
import { Tooltip } from '@myua/kit';
import { TM } from '../tokens';
import { HelpIcon } from '../icons';

// Small styled primitives ported from the design sample. Kept local to the
// Magic Studio so the tool reads as a single cohesive surface that matches
// the mockup, independent of the surrounding antd UI.

type BtnKind = 'primary' | 'secondary' | 'ghost' | 'magic' | 'danger';
type BtnSize = 'sm' | 'md';

const FONT = '"Noto Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export const MagicBtn: React.FC<{
  kind?: BtnKind;
  size?: BtnSize;
  icon?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
  children?: ReactNode;
}> = ({ kind = 'primary', size = 'md', icon, disabled, onClick, style = {}, children }) => {
  const h = size === 'sm' ? 24 : 32;
  const base: CSSProperties = {
    height: h,
    padding: `0 ${size === 'sm' ? 10 : 14}px`,
    fontFamily: FONT,
    fontSize: size === 'sm' ? 12 : 14,
    fontWeight: 600,
    borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid transparent',
    transition: 'all .12s ease',
    opacity: disabled ? 0.45 : 1,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  };
  const variants: Record<BtnKind, CSSProperties> = {
    primary: { background: TM.blue, color: '#fff', borderColor: TM.blue },
    secondary: { background: '#fff', color: TM.text, borderColor: TM.borderStrong },
    ghost: { background: 'transparent', color: TM.text, borderColor: 'transparent' },
    magic: {
      background: TM.magicGradient,
      color: '#fff',
      borderColor: 'transparent',
      boxShadow: '0 1px 0 rgba(255,255,255,0.2) inset, 0 2px 6px rgba(24,98,209,0.25)',
    },
    danger: { background: '#fff', color: TM.orange, borderColor: TM.borderStrong },
  };
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{ ...base, ...variants[kind], ...style }}
    >
      {icon}
      {children}
    </button>
  );
};

// Tab button for the studio's Generate | History switcher. The active tab gets
// the faint-blue pill; an optional count badge sits after the label.
export const StudioTab: React.FC<{
  active: boolean;
  icon: ReactNode;
  label: string;
  badge?: number;
  onClick: () => void;
}> = ({ active, icon, label, badge, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      height: 32,
      padding: '0 12px',
      borderRadius: 7,
      cursor: 'pointer',
      border: 'none',
      background: active ? TM.blueFaint : 'transparent',
      color: active ? TM.blue : TM.textMute,
      fontFamily: FONT,
      fontSize: 13,
      fontWeight: active ? 700 : 500,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 7,
    }}
  >
    {icon}
    {label}
    {badge != null && badge > 0 && (
      <span
        style={{
          minWidth: 17,
          height: 17,
          padding: '0 5px',
          borderRadius: 9,
          background: active ? TM.blue : TM.borderStrong,
          color: '#fff',
          fontSize: 10,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {badge}
      </span>
    )}
  </button>
);

export const Section: React.FC<{
  title: string;
  hint?: ReactNode;
  right?: ReactNode;
  children?: ReactNode;
}> = ({ title, hint, right, children }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: TM.textMute,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {title}
      </div>
      {hint != null && <HintIcon text={hint} />}
      <div style={{ flex: 1 }} />
      {right}
    </div>
    {children}
  </div>
);

export const Field: React.FC<{ label: string; children?: ReactNode; style?: CSSProperties }> = ({
  label,
  children,
  style,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10, ...style }}>
    <div style={{ fontSize: 11, color: TM.textFaint }}>{label}</div>
    {children}
  </div>
);

// Lightweight hover popover used inside the studio (kept local so it matches the
// module's custom surface rather than the surrounding antd overlays). Opens on
// hovering the trigger and — when `interactive` — stays open while the pointer
// is over the popover itself, so its content (e.g. an action button) is usable.
export const HoverPopover: React.FC<{
  content: ReactNode;
  children: ReactNode;
  placement?: 'top' | 'bottom';
  align?: 'left' | 'right';
  width?: number;
  interactive?: boolean;
}> = ({ content, children, placement = 'top', align = 'left', width = 260, interactive }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const show = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    // A short grace period lets the pointer travel from trigger to popover.
    closeTimer.current = setTimeout(() => setOpen(false), interactive ? 120 : 0);
  };

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      {open && (
        <div
          onMouseEnter={interactive ? show : undefined}
          onMouseLeave={interactive ? hide : undefined}
          style={{
            position: 'absolute',
            ...(placement === 'top'
              ? { bottom: '100%', marginBottom: 6 }
              : { top: '100%', marginTop: 6 }),
            ...(align === 'left' ? { left: 0 } : { right: 0 }),
            width,
            zIndex: 80,
            background: '#fff',
            border: `1px solid ${TM.border}`,
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
            padding: 10,
            cursor: 'default',
            textTransform: 'none',
            letterSpacing: 0,
          }}
        >
          {content}
        </div>
      )}
    </span>
  );
};

// A small "?" affordance next to a section title. Hovering reveals the section's
// help text — keeps the panels uncluttered while the guidance stays one hover
// away. Uses the kit Tooltip (portalled to <body>) so it isn't clipped by the
// panels' own overflow scrolling.
export const HintIcon: React.FC<{
  text: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  width?: number;
}> = ({ text, placement = 'bottom', width = 260 }) => (
  <Tooltip title={text} placement={placement} overlayStyle={{ maxWidth: width }}>
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: TM.textFaint,
        cursor: 'help',
      }}
      aria-label="Help"
    >
      <HelpIcon size={13} />
    </span>
  </Tooltip>
);
